import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { me, type Permission } from "./api";
import { permissionsOfMe } from "./permissions";

/**
 * Droits du compte courant — admin V1.27 : un bouton n'apparaît que si le
 * serveur accepte l'action. V1.51 (Gestion des droits) : ils viennent du
 * serveur (`me().permissions`), réglages du Super-admin compris, et non plus de
 * listes de rôles en dur. Tant que `me` charge, tout est faux : rien n'est
 * proposé à tort.
 */
export function useRights() {
  const meQ = useQuery({ queryKey: ["admin", "me"], queryFn: me });
  const perms = useMemo(() => permissionsOfMe(meQ.data), [meQ.data]);
  const can = (p: Permission) => perms.has(p);
  return {
    role: meQ.data?.role,
    perms,
    can,
    /** content.read : voir les contenus et les applications. */
    canReadContent: can("content.read"),
    /** content.write : créer, modifier, importer, envoyer des médias. */
    canEdit: can("content.write"),
    /** content.publish : publier / dépublier (POI, parcours, expositions, applications). */
    canPublish: can("content.publish"),
    /** billing.manage : partenaires (lecture, diagnostic), achats et accès, codes comptoir. */
    canBilling: can("billing.manage"),
    /** billing.testCodes : créer et supprimer des codes de test. */
    canTestCodes: can("billing.testCodes"),
    /** billing.exempt : exempter un billet de l'usage unique. */
    canExempt: can("billing.exempt"),
    /** billing.renewUnlimited : renouveler un accès au-delà de la limite du comptoir. */
    canRenewUnlimited: can("billing.renewUnlimited"),
    /** reporting.finance : reporting billetterie et redevance. */
    canFinance: can("reporting.finance"),
    /** audit.read : journal d'audit. */
    canAudit: can("audit.read"),
    /** users.manage : comptes du back-office, Questions fréquentes. */
    canManageUsers: can("users.manage"),
    /** demoAccess.manage : accès démo et previews. */
    canManageDemo: can("demoAccess.manage"),
    /** rights.manage : Gestion des droits (Super-admin). */
    canManageRights: can("rights.manage"),
    /** rights.manageUser : Droits par personne (Super-admin, verrouillé). */
    canManageUserRights: can("rights.manageUser"),
    /** mcp.manageTokens : fabriquer ses jetons d'API (serveur MCP). */
    canManageApiTokens: can("mcp.manageTokens"),
    /** ndp.manage : suivi et facturation des groupes Notre-Dame (Super-admin, verrouillé). */
    canManageNdp: can("ndp.manage"),
    /** tenant.manage : clients, revendeurs, clés, catalogue, règles de redevance (Super-admin). */
    isSuperAdmin: can("tenant.manage"),
  };
}
